import React, { useMemo, memo, useCallback, useState } from "react"
import { Accordion, AccordionSummary, AccordionDetails, Typography, Divider, IconButton, Checkbox, Toolbar } from "@mui/material"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender, getFilteredRowModel, ColumnResizeMode } from "@tanstack/react-table"
import { CSVLink } from "react-csv"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { Button1 } from "../Buttons/Button1"
import Filter from "./TanStackFilterFunction"
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import TanstackColumnDropDown from "./TanstackColumnDropDown"
import { motion, AnimatePresence } from "framer-motion"
import { getFacetedRowModel, getFacetedUniqueValues } from "@tanstack/react-table"

import "./TanstackTable.css"

export const multiSelectOrFilterFn = (row, columnId, filterValues, filterMeta, formatValue) => {
  if (!Array.isArray(filterValues) || filterValues.length === 0) return true // no filter => include all

  const cellValue = row.getValue(columnId)
  const formatted = formatValue ? formatValue(cellValue) : cellValue?.toString()
  // return filterValues.includes(formatted) // OR logic

  return filterValues.some((v) => v == formatted)
}

export const smartFilterFn = (row, columnId, filterValues) => {
  const colDef = row.column.columnDef
  const isNumber = colDef?.meta?.isNumber

  // No filter applied → allow all
  if (!filterValues || (Array.isArray(filterValues) && filterValues.length === 0)) return true

  const rawValue = row.getValue(columnId)
  const formatValue = colDef?.meta?.formatValue
  const value = formatValue ? formatValue(rawValue) : rawValue

  // ================================
  // 🔢 NUMERIC FILTER MODE
  // ================================
  if (isNumber) {
    // filterValues will be: { type: "gt" | "lt" | "eq" | "gte" | "lte", number: value }
    if (typeof filterValues !== "object") return true

    const { type, number } = filterValues
    const num = Number(value)
    const target = Number(number)

    if (isNaN(num) || isNaN(target)) return true

    switch (type) {
      case "gt":
        return num > target
      case "lt":
        return num < target
      case "eq":
        return num == target
      case "gte":
        return num >= target
      case "lte":
        return num <= target
      default:
        return true
    }
  }

  // ================================
  // 🟦 MULTI-SELECT TEXT FILTER (OR logic)
  // ================================
  if (Array.isArray(filterValues)) {
    return filterValues.some((v) => v == value)
  }

  // ================================
  // 🔤 TEXT SEARCH FALLBACK
  // ================================
  if (typeof filterValues === "string") {
    return value?.toString().toLowerCase().includes(filterValues.toLowerCase())
  }

  return true
}

const CustomTable = ({
  maxHeight,
  table,
  getRowClassName,
  total_records,
  handleBulkSelection,
  bulkMode,
  BulkActionComponent,
  selectedIds,
  selectedRows,
  selectedCount,
  handleBulkChange,
  bulkActionChanges,
  bulkBg,
  allowBulk,
}) => {
  const isAnyColumnHidden = table.getAllLeafColumns().some((col) => !col.getIsVisible())

  function exportTableToXLSX(table, filename = "table_data.xlsx") {
    // Get all filtered rows, ignoring pagination
    const rows = table.getPrePaginationRowModel().rows

    // Prepare plain data
    const data = rows.map((row) => {
      const rowData = {}
      row.getVisibleCells().forEach((cell) => {
        const colId = cell.column.id
        rowData[cell.column.columnDef.header] = cell.getValue()
      })
      return rowData
    })

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data")

    // Export to file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    })
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), filename)
  }
  const isPinned = (con) => {
    return con.column.id === "Status" ? true : false
  }

  const tableContainerRef = React.useRef < HTMLDivElement > null

  const parentRef = React.useRef(null)

  const { rows } = table.getRowModel()

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    enabled: true,
  })

  const virtualRows = virtualizer.getVirtualItems() || []
  const totalSize = virtualizer.getTotalSize()

  const paddingTop = virtualRows?.length > 0 ? virtualRows?.[0]?.start || 0 : 0
  const paddingBottom = virtualRows?.length > 0 ? totalSize - (virtualRows?.at(-1)?.end || 0) : 0
  return (
    <>
      {BulkActionComponent && (
        <div style={{ display: "flex", justifyContent: "end", position: "relative", height: "50px", background: "#FFF" }} className="flex justify-end">
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 240, damping: 18 }}
            style={{ width: "100%" }}
          >
            <AnimatePresence mode="wait">
              <Toolbar style={{ position: "absolute", background: `${bulkBg}`, borderRadius: 8, top: "0px", right: "20px", minHeight: "45px", display: "flex", gap: "10px" }}>
                {table.getSelectedRowModel().rows.length > 0 ? (
                  <motion.div
                    key="selected-rows"
                    initial={{ opacity: 0, y: -12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 240, damping: 18 }}
                    style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        background: "#eef6ff",
                        color: "#0b6efd",
                        border: "1px solid #bfdcff",
                        padding: "3px 10px",
                        borderRadius: "30px",
                        letterSpacing: 0.3,
                        width: "160px",
                        justifyContent: "center",
                      }}
                    >
                      {table.getSelectedRowModel().rows.length} Row(s) selected
                    </Typography>

                    {BulkActionComponent && (
                      <BulkActionComponent
                        handleBulkChange={handleBulkChange}
                        selectedIds={selectedIds}
                        selectedCount={selectedCount}
                        selectedRows={selectedRows || []}
                        clearSelection={() => table.resetRowSelection()}
                      />
                    )}
                    {/* <Button1 variant="contained" onClick={bulkMarkEmployeeError} sx={{ mr: 1 }} customStyle={{ maxWidth: "150px", height: "35px", fontSize: "12px" }}>
                Change Status
              </Button1> */}
                  </motion.div>
                ) : (
                  bulkMode && (
                    <motion.div
                      key="no-selection"
                      initial={{ opacity: 0, y: -12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 240, damping: 18 }}
                      style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          background: "#eef6ff",
                          color: "#0b6efd",
                          border: "1px solid #bfdcff",
                          padding: "3px 10px",
                          borderRadius: "30px",
                          letterSpacing: 0.3,
                          width: "160px",
                          justifyContent: "center",
                        }}
                      >
                        Select Rows
                      </Typography>
                    </motion.div>
                  )
                )}
                {allowBulk && (
                  <Button1 variant="outline" onClick={handleBulkSelection} customStyle={{ width: bulkActionChanges ? "190px" : "160px", height: "30px", fontSize: "12px" }}>
                    {bulkMode ? (bulkActionChanges ? "Apply changes" : "Cancel") : "Multi Select"}
                  </Button1>
                )}
                {isAnyColumnHidden && (
                  <Button1 variant="outline" onClick={() => table.resetColumnVisibility()} customStyle={{ width: "220px", height: "30px", fontSize: "12px" }}>
                    Show Hidden Columns
                  </Button1>
                )}
              </Toolbar>
            </AnimatePresence>
          </motion.div>
        </div>
      )}

      <div
        style={{
          // height: `calc(${maxHeight} - 130px)`,
          overflow: "auto",
          overflowX: "auto",
          background: "var(--va-card-3)",
          border: "1px solid var(--va-card-4-border)",
        }}
        ref={parentRef}
      >
        <div
          style={{
            height: `calc(${maxHeight} - 270px`,
            // height: `${virtualizer.getTotalSize()}px`,
            maxHeight: `calc(${maxHeight} - 130px)`,
            paddingBottom: "120px",
            width: "100%",
            position: "relative",
          }}
        >
          <table
            className="tanstack-table"
            style={{
              width: "100%",
              tableLayout: "fixed",
              borderSpacing: "0px",
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  style={{
                    height: "50px",
                    //   border: "4px grey solid",
                    //   position: "sticky",
                    //   top: 0,
                  }}
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      colSpan={header.colSpan}
                      key={header.id}
                      className={`tanstack-table-th ${header.column.columnDef.meta?.className || ""} `}
                      style={{
                        position: isPinned(header) ? "sticky" : "sticky",
                        top: 0,
                        left: isPinned(header) ? 0 : undefined,
                        zIndex: isPinned(header) ? 3 : 2,
                        background: isPinned(header) ? "white" : "white", // or any consistent color
                        //   padding: "4px",
                        width: `${header.getSize()}px`,
                        userSelect: "none",
                        // borderRight: "1px solid #ccc",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        borderBottom: "1px solid",
                        padding: "0px 40px 0px 5px",
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}

                      {header.column.getCanResize() ? (
                        <div
                          className="seperator"
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            height: "100%",
                            width: "5px",
                            cursor: "col-resize",
                            userSelect: "none",
                            touchAction: "none",
                          }}
                        />
                      ) : (
                        <div
                          className="seperator"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            height: "100%",
                            width: "5px",
                            cursor: "default",
                            userSelect: "none",
                            touchAction: "none",
                          }}
                        ></div>
                      )}

                      {header.column.getCanFilter() && (
                        <div className="more-icon-wrapper">
                          {/* <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-haspopup="true"
                            onClick={(event) => {
                              event.stopPropagation()
                              handleOtherOptionsClick(event, header.column)
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton> */}
                          <TanstackColumnDropDown column={header.column} table={table} />
                        </div>

                        // <div>
                        //   <Filter column={header.column} table={table} />
                        // </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: paddingTop }} />
                </tr>
              )}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index]

                return (
                  // <tr key={row.id} className={`table-row ${getRowClassName?.(row.original) ?? ""} row-enter`} style={{ height: 30, transitionDelay: `${virtualRow.index * 5}ms` }}>
                  <motion.tr
                    key={row.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className={getRowClassName?.(row.original) ?? ""}
                    style={{ height: 30 }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: "2px 8px",
                          position: isPinned(cell) ? "sticky" : undefined,
                          left: isPinned(cell) ? 0 : undefined,
                          background: isPinned(cell) ? "white" : undefined,
                          zIndex: isPinned(cell) ? 1 : undefined,
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textAlign: "center",
                          maxWidth: `${cell.column.getSize()}px`,
                          borderBottom: "1px solid #cbcbcb",
                        }}
                        className={cell.column.columnDef.meta?.className || ""}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    {/* </tr> */}
                  </motion.tr>
                )
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: paddingBottom }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 8px",
          background: "var(--va-card-4)",
          borderTop: "1px solid var(--va-card-4-border)",
        }}
      >
        <div>
          <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
            ⏮ First
          </button>
          <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
            ◀ Prev
          </button>
          <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Next ▶
          </button>
          <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
            Last ⏭
          </button>
        </div>
        <span>
          Page{" "}
          <strong>
            {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </strong>
        </span>

        <span>
          Showing Records {table.getRowModel().rows.length} of {total_records}
        </span>
        <select value={table.getState().pagination.pageSize} onChange={(e) => table.setPageSize(Number(e.target.value))}>
          {[10, 100, 250, 500, 1000].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>

        <span>
          {/* <button
            onClick={() => exportTableToXLSX(table)}
            style={{
              padding: "6px 12px",
              background: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 4,
            }}
          >
            Download XLSX
          </button> */}

          <Button1
            onClick={() => exportTableToXLSX(table)}
            style={{
              padding: "6px 12px",
            }}
          >
            Download
          </Button1>
        </span>
      </div>
    </>
  )
}

function TanStackTable({
  maxHeight,
  isAccordion = true,
  title,
  rows,
  isExpanded = false,
  tableKey,
  columns,
  getRowClassName,
  pageSize,
  total_records,
  BulkActionComponent,
  bulkBg = "#f3f4f6",
  allowBulk = true,
}) {
  const [rowSelection, setRowSelection] = useState({}) // checkbox multi-select
  // const [activeRow, setActiveRow] = useState(null) // click row highlight
  const [bulkMode, setBulkMode] = useState(false) // show checkboxes toggle
  const [bulkActionChanges, setBulkActionChanges] = React.useState(false)

  const getRowId = useCallback((row) => tableKey.map((_k) => row[_k]).join("-"), [tableKey])

  const [columnFilters, setColumnFilters] = React.useState([])

  const columnsWithSelect = useMemo(() => {
    const selectCol = {
      id: "select",
      enableResizing: false,
      getCanResize: () => false,
      size: bulkMode ? 40 : 0,
      minSize: bulkMode ? 40 : 0,
      maxSize: bulkMode ? 40 : 0,
      header: () => (
        <div>
          {bulkMode && (
            <Checkbox
              // checked={Object.keys(rowSelection).length === rows.length}
              checked={Object.keys(rowSelection).length === table.getRowModel().rows.length}
              indeterminate={Object.keys(rowSelection).length > 0 && Object.keys(rowSelection).length < table.getRowModel().rows.length}
              onChange={(e, v) => {
                const isChecked = e.target.checked
                if (isChecked) {
                  const allRowIds = {}
                  table.getRowModel().rows.forEach((row) => {
                    allRowIds[row.id] = true
                  })
                  setRowSelection(allRowIds)
                } else {
                  setRowSelection({})
                }
              }}
            />
          )}
        </div>
      ),
      cell: ({ row }) => (
        <div style={{ padding: 0, margin: 0, width: "100%" }}>{bulkMode ? <Checkbox checked={row.getIsSelected()} onChange={(e) => row.toggleSelected(e.target.checked)} /> : null}</div>
      ),
      meta: { className: "no-padding-cell" },
    }
    return [selectCol, ...columns]
  }, [columns, bulkMode, rowSelection, rows])

  const table = useReactTable({
    data: rows,
    columns: columnsWithSelect,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      rowSelection: rowSelection,
      columnFilters: columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getRowId: (row, index) => {
      const baseId = getRowId(row)
      if (!baseId || baseId.trim() === "") {
        return `row-${index}`
      }
      return baseId
    },
    enableColumnResizing: true,
    enablePinning: true,
    columnResizeMode: "onChange",
    manualPagination: false,
    autoResetPageIndex: false,

    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: pageSize ? pageSize : 10,
        pageIndex: 0,
      },
    },
    meta: {
      reloadData: () => { },
    },
  })

  const selectedIds = table.getSelectedRowModel().flatRows.map((r) => r.id || r.original.id)
  const selectedRows = table.getSelectedRowModel().flatRows.map((r) => r.original)
  const selectedCount = selectedIds.length

  const handleBulkSelection = () => {
    setBulkMode((prev) => !prev)

    if (bulkMode) {
      setRowSelection({})
      setBulkActionChanges(false)
    }
  }

  const handleBulkChange = () => {
    setBulkActionChanges(true)
  }

  return isAccordion ? (
    <>
      <Accordion defaultExpanded={isExpanded} sx={{ background: "rgba(0,0,0,0.5)" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div
            style={{
              display: "flex",
              width: "100%",
              marginRight: "20px",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ color: "white", flex: "1" }}>
              {title}
            </Typography>
            <Typography variant="body" sx={{ color: "white" }}>
              Total Transactions: <span style={{ color: "black", fontWeight: "bold" }}>{rows.length}</span>
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <CustomTable
            table={table}
            maxHeight={maxHeight}
            getRowClassName={getRowClassName}
            total_records={total_records}
            handleBulkSelection={handleBulkSelection}
            bulkMode={bulkMode}
            bulkBg={bulkBg}
            allowBulk={allowBulk}
          />
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ marginY: 4 }} />
    </>
  ) : (
    <CustomTable
      handleBulkChange={handleBulkChange}
      table={table}
      maxHeight={maxHeight}
      getRowClassName={getRowClassName}
      total_records={total_records}
      handleBulkSelection={handleBulkSelection}
      bulkMode={bulkMode}
      selectedIds={selectedIds}
      selectedRows={selectedRows}
      selectedCount={selectedCount}
      BulkActionComponent={BulkActionComponent}
      bulkActionChanges={bulkActionChanges}
      bulkBg={bulkBg}
      allowBulk={allowBulk}
    />
  )
}

export default React.memo(TanStackTable, (prevProps, nextProps) => {
  return prevProps.rows === nextProps.rows && prevProps.columns === nextProps.columns && prevProps.tableKey === nextProps.tableKey && prevProps.isAccordion === nextProps.isAccordion
})
